// Authentication Types

export interface LoginRequest {
  request_id: string; // AES encryption key
  username: string; // Email or username (AES encrypted)
  password: string; // AES encrypted
}

export interface LoginResponse {
  success: boolean;
  data?: {
    access_token?: string;
    token_type?: string;
    expires_in: number;
    refresh_token?: string;
    csrf_token?: string;
    user_info: UserInfo;
  };
  description?: string;
  error?: ApiError;
}

export interface UserInfo {
  id: string;
  email: string;
  given_name: string;
  family_name: string;
  full_name?: string;
  groups: UserGroup[];
  profile_image?: string;
  mobile_number?: string;
  status: UserStatus;
  employee_id?: string;
  meta_data?: Record<string, any>;
}

export interface UserRole {
  role_key: string;
  role_name: string;
}

export interface UserGroup {
  group_key: string;
  group_name: string;
  roles: UserRole[];
}

export type UserMode = 'admin' | 'learner';

export interface UserModeInfo {
  hasAdminRole: boolean;
  hasLearnerRole: boolean;
  hasBothRoles: boolean;
  currentMode: UserMode;
  preferredMode: UserMode;
  hasValidRole?: boolean;
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION'
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  data?: {
    access_token?: string;
    token_type?: string;
    expires_in: number;
    refresh_token?: string;
    csrf_token?: string;
  };
  error?: ApiError;
}

export interface CurrentUserResponse {
  success: boolean;
  data?: {
    user: UserInfo;
    permissions: string[];
    tenant_key: string;
    last_login: string;
    registration_type: string;
    csrf_token?: string;
    /** Present only when the session came from an LMS (LTI) launch. */
    auth_source?: 'lti';
  };
  error?: ApiError;
}

// Registration Types

export interface SelfRegistrationRequest {
  request_id: string; // AES encryption key
  email: string; // AES encrypted
  password: string; // AES encrypted
  confirm_password: string; // AES encrypted
  given_name: string;
  family_name: string;
  mobile_number?: string;
  accept_terms?: boolean;
  additional_info?: Record<string, any>;
  referral_code?: string;
}

export interface InviteRegistrationRequest {
  request_id: string; // AES encryption key
  invitation_token: string;
  password: string; // AES encrypted
  confirm_password: string;
  given_name?: string;
  family_name?: string;
  mobile_number?: string;
  additional_info?: Record<string, any>;
}

export interface RegistrationResponse {
  success: boolean;
  data?: {
    success: boolean;
    message: string;
    user_id: string;
    access_token: string | null;    // Null for self-registration
    token_type: string;
    expires_in: number | null;      // Null for self-registration
    refresh_token: string | null;   // Null for self-registration
    user_info: UserInfo;
    email_verification_required: boolean;
    redirect_url: string;
  };
  error?: ApiError;
}

export interface RegistrationValidationResponse {
  success: boolean;
  data?: {
    allowed: boolean;
    registration_type: 'self' | 'invite' | 'not_allowed';
    message: string;
    business_settings?: {
      company_name: string;
      logo_url: string;
      terms_url: string;
      privacy_url: string;
    };
    invitation_info?: {
      email: string;
      full_name: string;
      given_name: string;
      family_name: string;
      groups: string[];
      expires_at: string;
      company_name: string;
      custom_metadata?: Record<string, any>;
    };
  };
  error?: ApiError;
}

// Password Management Types

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  new_password: string;
  confirm_password: string;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export interface PasswordStrengthResponse {
  success: boolean;
  data?: {
    strength: 'weak' | 'medium' | 'strong' | 'very_strong';
    score: number;
    requirements: {
      min_length: boolean;
      has_uppercase: boolean;
      has_lowercase: boolean;
      has_number: boolean;
      has_special: boolean;
    };
    suggestions: string[];
  };
}

// Email Verification Types

export interface EmailVerificationRequest {
  verification_token: string;
}

export interface ResendVerificationRequest {
  email: string;
}

// Common Types

export interface ApiError {
  http_code?: number;
  error?: string;
  error_description: string;
  meta_data?: any;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  description?: string;
  error?: ApiError;
  pagination?: {
    total_records: number;
    current_page: number;
    records_per_page: number;
  };
}

// Token Storage Keys
export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'skillzen_access_token',
  REFRESH_TOKEN: 'skillzen_refresh_token',
  USER_DATA: 'skillzen_user',
  TENANT_KEY: 'skillzen_tenant',
  TOKEN_EXPIRY: 'skillzen_token_expiry'
} as const;

// Error Codes
export const ERROR_CODES = {
  // Authentication Errors
  AUTH_001: 'Invalid credentials',
  AUTH_002: 'Account locked due to failed attempts',
  AUTH_003: 'Account disabled',
  AUTH_004: 'Token expired',
  AUTH_005: 'Token invalid',
  AUTH_ERROR: 'General authentication error',
  
  // Registration Errors
  SELF_REGISTRATION_DISABLED: 'Self-registration not allowed',
  EMAIL_ALREADY_EXISTS: 'Email already registered',
  INVALID_INVITATION_TOKEN: 'Invalid invitation token',
  INVITATION_EXPIRED: 'Invitation has expired',
  PASSWORDS_DO_NOT_MATCH: 'Password confirmation mismatch',
  TERMS_NOT_ACCEPTED: 'Terms and conditions not accepted',
  REGISTRATION_ERROR: 'General registration error',
  
  // Password Reset Errors
  INVALID_TOKEN: 'Invalid or expired reset token',
  TOKEN_EXPIRED: 'Reset token has expired',
  WEAK_PASSWORD: 'Password doesn\'t meet requirements',
  SAME_PASSWORD: 'New password same as current',
  TOO_MANY_ATTEMPTS: 'Rate limit exceeded',
  RESET_ERROR: 'General password reset error'
} as const;